import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useUserInput } from '../contexts/UserInputProvider';
import { MortgageData } from './InputForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFloppyDisk } from '@fortawesome/free-solid-svg-icons'

interface SaveInputsProps {
    mortgageData: MortgageData;
    whichMortgage: string;
    isCompareVisible: boolean;
}

export type SaveInputsRef = { // For clearing inputs
    resetForm: () => void;
};

const SaveInputs = forwardRef<SaveInputsRef, SaveInputsProps & { onSelectMortgage: (id: string, data: MortgageData) => void }>(
    ({ mortgageData, whichMortgage, isCompareVisible, onSelectMortgage }, ref) => {
    const [saveName, setSaveName] = useState<string>("");
    const [selectKey, setSelectKey] = useState<number>(Math.random());
    const { userInputs, addMortgageInput, msg, setMsg, errorMsg, setErrorMsg, fetchAll }: any = useUserInput();

    const [loading, setLoading] = useState(false);
    const [mortgageInputOptions, setMortgageInputOptions] = useState<{ label: string, id: string }[]>([]);
    const [mortgageInputData, setMortgageInputData] = useState<{ id: string, data: MortgageData }[]>([]);

    useImperativeHandle(ref, () => ({
        resetForm: () => {
            setSaveName(""); // Reset saveName or any other state you wish to reset
            // Add more reset logic here if needed
        },
    }));

    useEffect(() => {
        handleFetch();
    }, []);

    useEffect(() => { // Automatically updates when user saves a mortgage
        const options = userInputs.map((input: { mortgage_name: any; id: any; }) => ({ label: input.mortgage_name, id: input.id }));
        setMortgageInputOptions(options);

        for (let i = 0; i < userInputs.length; i++) {
            const data: MortgageData = {
                mortgageType: userInputs[i].mortgage_type,
                interestFrequency: userInputs[i].interest_frequency,
                interestValue: userInputs[i].interest_value,
                mortgageAmountValue: userInputs[i].mortgage_amount,
                mortgageTermValue: userInputs[i].mortgage_term,
                repaymentFrequency: userInputs[i].repayment_frequency,
                showOverpayments: userInputs[i].show_overpayments,
                overpaymentsValue: userInputs[i].overpayments_value,
                depositValue: userInputs[i].deposit_value,
                showOffset: userInputs[i].show_offset,
                offsetValue: userInputs[i].offset_value,
                showEquity: userInputs[i].show_equity,
                housePriceInterestValue: userInputs[i].house_price_interest_value,
                showAdvanced: userInputs[i].show_advanced,
                offsetRegular: userInputs[i].offset_regular,
                addBOE: userInputs[i].add_boe,
                productFee: userInputs[i].product_fee,
                useNominal: userInputs[i].use_nominal,
                date: String(userInputs[i].date || ''),
            }
            mortgageInputData.push({ id: userInputs[i].id, data: data });
        }
    }, [userInputs]);

    const handleFetch = async () => {
        try {
            setErrorMsg("");
            await fetchAll();
        } catch (error) {
            setErrorMsg((error as Error).message);
        }
    };

    const handleSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();

        try {
            setErrorMsg("");
            if (saveName === '') {
                setErrorMsg("Please fill in the field");
                return;
            }
            const mortgageInputs = {
                mortgage_name: saveName,
                mortgage_type: mortgageData.mortgageType,
                interest_frequency: mortgageData.interestFrequency,
                interest_value: mortgageData.interestValue,
                mortgage_amount: mortgageData.mortgageAmountValue,
                mortgage_term: mortgageData.mortgageTermValue,
                repayment_frequency: mortgageData.repaymentFrequency,
                show_overpayments: mortgageData.showOverpayments,
                overpayments_value: mortgageData.overpaymentsValue,
                deposit_value: mortgageData.depositValue,
                show_offset: mortgageData.showOffset,
                show_equity: mortgageData.showEquity,
                house_price_interest_value: mortgageData.housePriceInterestValue,
                show_advanced: mortgageData.showAdvanced,
                offset_regular: mortgageData.offsetRegular,
                add_boe: mortgageData.addBOE,
                offset_value: mortgageData.offsetValue,
                product_fee: mortgageData.productFee,
                use_nominal: mortgageData.useNominal,
                date: mortgageData.date ? new Date(mortgageData.date).toISOString() : null,
            }
            console.log(mortgageInputs)
            setLoading(true);
            await addMortgageInput(mortgageInputs);
            const modal = document.getElementById('save_modal') as HTMLDialogElement;
            if (modal) {
                modal.close();
            }
        } catch (error) {
            setErrorMsg((error as Error).message);
        }
        setLoading(false);
    };

    const handleMortgageInputChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectKey(Math.random());
        console.log(saveName)
        const selectedMortgageData: any = userInputs.find((mortgage: { id: any; }) => Number(event.currentTarget.value) === Number(mortgage.id));
        if (selectedMortgageData) {
            console.log(event.currentTarget.value)
            setSaveName(event.currentTarget.value)
            const data: MortgageData = {
                mortgageType: selectedMortgageData.mortgage_type,
                interestFrequency: selectedMortgageData.interest_frequency,
                interestValue: selectedMortgageData.interest_value,
                mortgageAmountValue: selectedMortgageData.mortgage_amount,
                mortgageTermValue: selectedMortgageData.mortgage_term,
                repaymentFrequency: selectedMortgageData.repayment_frequency,
                showOverpayments: selectedMortgageData.show_overpayments,
                overpaymentsValue: selectedMortgageData.overpayments_value,
                depositValue: selectedMortgageData.deposit_value,
                showOffset: selectedMortgageData.show_offset,
                offsetValue: selectedMortgageData.offset_value,
                showEquity: selectedMortgageData.show_equity,
                housePriceInterestValue: selectedMortgageData.house_price_interest_value,
                showAdvanced: selectedMortgageData.show_advanced,
                offsetRegular: selectedMortgageData.offset_regular,
                addBOE: selectedMortgageData.add_boe,
                productFee: selectedMortgageData.product_fee,
                useNominal: selectedMortgageData.use_nominal,
                date: selectedMortgageData.date,
            }
            onSelectMortgage(event.currentTarget.value, data);
            // console.log(selectedMortgageData)
            
        }
        
    };

    const handleSaveNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSaveName(event.target.value);
    };

    return (
        <>
        <div className={"grid card rounded-box bg-base-300"}>
            <div className="card-body p-1">
            <h2 className="card-title font-bold"><FontAwesomeIcon icon={faFloppyDisk} className="ml-2 mr-1" />Saved Mortgages {!isCompareVisible ? '' :  whichMortgage.charAt(whichMortgage.length - 1)}</h2>
                <select key={selectKey} value={saveName} onChange={handleMortgageInputChange} className="select select-bordered w-full">
                    <option disabled selected value={""}>Select your saved mortgage {!isCompareVisible ? '' : 'for ' + whichMortgage}</option>
                    {mortgageInputOptions.map((option) => (
                        <option value={option.id}>{option.label}</option>
                    ))}
                </select>
                <button className="btn-primary font-semibold p-3 rounded-full flex items-center justify-center" onClick={() => {
                    const modal = document.getElementById('save_modal') as HTMLDialogElement;
                    if (modal) {
                        modal.showModal();
                    }
                }}>{!isCompareVisible ? 'Save current inputs' : 'Save ' + whichMortgage + ' inputs'}</button>
                </div>
            </div>
            <dialog id="save_modal" className="modal">
                <div className="modal-box">
                    <form method="dialog">
                        <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
                    </form>
                    <div className="grid card rounded-box ">
                        <div className="px-4 py-5 sm:p-6 space-y-4">
                            <h2 className="text-center text-2xl font-bold mb-4">Save inputs</h2>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Name</label>
                                <input required onChange={handleSaveNameChange} className="input input-bordered w-full pl-4 bg-white" />
                            </div>
                            {errorMsg && (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                                <strong className="font-bold">Error</strong>
                                <span className="block sm:inline"> {errorMsg}</span>
                                <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setErrorMsg("")}>
                                    <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><title>Close</title><path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" /></svg>
                                </span>
                            </div>
                        )}
                            <div className="text-center mt-2">
                                <button type="submit" onClick={handleSubmit} className="w-1/2 text-xl font-semibold p-2 btn-primary rounded-full mt-4 md:mt-auto items-center justify-center">
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </dialog >
        </>
    );
});

export default SaveInputs;